import { useMemo } from 'react';
import type { BaseIntegrationEntity, IntegrationProviders } from '@vy/types';
import { withDefault, type Optional } from '@vy/utils';

type IntegrationEntityFinder<IntegrationEntityType extends BaseIntegrationEntity = BaseIntegrationEntity> = (
  integrationEntities: BaseIntegrationEntity[],
) => Optional<IntegrationEntityType>;

type UseIntegrationEntitiesOptions<
  IntegrationEntityType extends BaseIntegrationEntity = BaseIntegrationEntity,
> = {
  integrationEntities?: BaseIntegrationEntity[];
} & (
  | {
      integrationProvider: IntegrationProviders;
    }
  | {
      filterFn: IntegrationEntityFinder<IntegrationEntityType>;
    }
);

export function useIntegrationEntities<
  IntegrationEntityType extends BaseIntegrationEntity = BaseIntegrationEntity,
>(options: UseIntegrationEntitiesOptions<IntegrationEntityType>) {
  const integrationEntities = withDefault(options.integrationEntities, []);

  const integrationEntity = useMemo(() => {
    if ('integrationProvider' in options) {
      return integrationEntities.find(
        (entity) => entity.integrationProvider === options.integrationProvider,
      ) as Optional<IntegrationEntityType>;
    }

    return options.filterFn(integrationEntities);
  }, [integrationEntities, options]);

  const integrationEntityIndex = useMemo(() => {
    if (!integrationEntity) {
      return -1;
    }

    return integrationEntities.findIndex(
      (entity) => entity.integrationProvider === integrationEntity.integrationProvider,
    );
  }, [integrationEntities, integrationEntity]);

  return {
    integrationEntity,
    integrationEntities,
    integrationEntityIndex,
  };
}
